import { Service } from 'constants/usercentrics';
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { isServiceAllowed } from 'components/Common/CookieNotification';
import { EmbedPlaceholder } from 'components/Shared/EmbedPlaceholder';
import Icon from 'components/ui/Icon';
import Link from 'components/ui/Link';
import { CookieContext } from 'context/cookie';
import tailwind from 'utils/breakpoints';
import { useAnalytics } from 'hooks/useAnalytics';
import { useTranslation } from 'hooks/useTranslation';

type Props = {
  latitude: string | number;
  longitude: string | number;
  locationName: string;
  addressLine?: string;
  addressLine1?: string;
  addressLine2?: string;
  cityName: string;
  countryName: string;
};

const LocationMap = ({
  addressLine,
  addressLine1,
  addressLine2,
  cityName,
  countryName,
  latitude,
  longitude,
}: Props): JSX.Element | null => {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const { cookies } = useContext(CookieContext);
  const [allowed, setAllowed] = useState(false);

  const [isMapOverlayVisible, setIsMapOverlayVisible] = useState(false);

  let address = addressLine;
  if (!address) {
    address =
      addressLine1 && addressLine2
        ? `${addressLine1}, ${addressLine2}`
        : `${addressLine1}${addressLine2}`;
  }

  const cityCountry =
    cityName && countryName
      ? `${cityName}, ${countryName}`
      : `${cityName}${countryName}`;

  useEffect(() => {
    const serviceAllowed = isServiceAllowed(cookies, Service.GOOGLE_MAPS);

    setAllowed(serviceAllowed.serviceStatus);
  }, [cookies]);

  return (
    <LazyLoad classNamePrefix="location-map" offset={200} once>
      <div className="border">
        {!allowed ? (
          <EmbedPlaceholder service={Service.GOOGLE_MAPS} />
        ) : (
          <div
            className="relative flex items-center justify-center"
            onMouseEnter={() => {
              setIsMapOverlayVisible(true);
            }}
            onMouseLeave={() => {
              setIsMapOverlayVisible(false);
            }}
          >
            <iframe
              width="100%"
              height="280"
              id="gmap_canvas"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(
                `${latitude},${longitude}`,
              )}&t=&z=17&ie=UTF8&iwloc=&output=embed`}
            />
            <div
              className={clsx(
                'h-full w-full bg-black absolute top-0 bottom-0 transition-opacity duration-300 ease-in-out',
                isMapOverlayVisible ? 'opacity-50' : 'opacity-0',
              )}
            />
            <div className={clsx('absolute', !isMapOverlayVisible && 'hidden')}>
              <div className="rounded bg-white p-4 top-0 m-6">
                <p className="text-charcoal text-center">
                  {t('viewOnGoogleMaps.question')}
                </p>
                <Link
                  href={`https://maps.google.com/maps?q=loc:${latitude},${longitude}`}
                >
                  <a
                    target="_blank"
                    onClick={() => {
                      trackEvent({
                        category: 'CTA',
                        action: 'CTA|Click',
                        label: 'CTA|Clinic|GoogleMaps',
                      });
                    }}
                    className="bg-secondary w-full rounded font-netto text-lg block text-center p-2 mt-6 leading-8 transition-colors hover:bg-yellowDark duration-300"
                  >
                    {t('viewOnGoogleMaps')}
                  </a>
                </Link>
              </div>
            </div>
          </div>
        )}
        <div className="p-3 flex">
          <Icon
            className="mr-3 w-4 shrink-0"
            type="LocationPin"
            color={tailwind.theme.backgroundColor.secondary}
          />
          <p>
            {address}
            {cityCountry && (
              <>
                {', '}
                <span className="font-bold">{cityCountry}</span>
              </>
            )}
          </p>
        </div>
      </div>
    </LazyLoad>
  );
};

export default LocationMap;
